/**
 * @file: SummaryModel.js
 * @author: liuye11(liuye11@baidu.com)
 */

import format from 'date-fns/format';
import subDays from 'date-fns/sub_days';
import uri from '@baidu/ve-ria/utils/uri';
import {purify} from '@baidu/ve-ria/utils/lang';
import BaseModel from '@baidu/ve-ria/views/BaseModel';
import {assign, omit, find, values, toString} from 'lodash';

const TAB_MAP = {
    component: {label: '组件', to: '/stats/material/report/component'},
    link: {label: '子链', to: '/stats/material/report/link'},
    frame: {label: '中间页', to: '/stats/material/report/frame'}
};
export default class Model extends BaseModel {

    /**
     * @override
     */
    title = '';
    crumbData = [];

    filter = {
        associatedType: null,
        associatedId: null,
        aderId: null,
        parentType: null,
        parentId: null,
        startDate: format(subDays(new Date(), 7), 'YYYYMMDD'),
        endDate: format(subDays(new Date(), 1), 'YYYYMMDD'),
        pageNo: 1,
        pageSize: 100,
        order: 'desc',
        orderBy: 'id'
    }

    tabs = [];
    selectDatasource = [];
    selectItem = {};
    materialType = '卡片';

    init() {
        super.init();
        this.cardRequester = this.api.statsCardList;
        this.frameRequester = this.api.statsFrameList;
        this.componentRequester = this.api.statsComponentList;
        this.crumbData.length = 0;
        this.tabs.length = 0;
        this.setStoreInfo('add');
    }

    async setStoreInfo(mod) {
        let query = omit(this.getRouteQuery({}), ['pageNo', 'pageSize']);
        assign(this.filter, query);

        // 根据路由中parentType设置一级面包屑信息
        if (query.parentType) {
            await this.setSummaryHeader(query.parentType, query.parentId, 1);
        }

        // 根据路由中associatedType设置次级面包屑信息，以及路由tab信息
        if (query.associatedType) {
            let tabs = await this.setSummaryHeader(query.associatedType, query.associatedId, 2);
            // 设置tab路由信息
            tabs.forEach(tab => {
                tab.routePath = uri.withQuery(tab.to, query);
                this.tabs.push(tab);
            });
        }
    }

    async setSummaryHeader(type, filterId, crumbLevel) {
        let tabs;
        switch (type) {
            case 'CARD':
                await this.setCardSummaryHeader();
                tabs = values(TAB_MAP);
                break;
            case 'FRAME':
                await this.setFrameSummaryHeader(crumbLevel);
                tabs = [TAB_MAP.component, TAB_MAP.link];
                break;
            case 'COMPONENT':
                await this.setComponentSummaryHeader();
                tabs = [TAB_MAP.link];
                break;
        }

        // 添加子面包屑
        let item = find(this.selectDatasource, item => toString(item.id) === filterId);
        if (item) {
            this.selectItem = item;
            this.crumbData.push({text: item.name, native: true});
        }
        return tabs;
    }

    async setCardSummaryHeader() {
        this.materialType = '卡片';
        this.crumbData.push({text: '卡片数据报告', to: '/stats/material/base/card/list', type: 'link'});

        let data = await this.cardRequester(this.prepareQuery());
        this.selectDatasource = data.result;
    }

    async setFrameSummaryHeader(crumbLevel) {
        this.materialType = '中间页';
        this.crumbData.push({text: '中间页数据报告', to: '/stats/material/base/frame/list', type: 'link'});

        let condition;
        if (crumbLevel === 1) {
            /**
             * 面包屑: 卡片 / 中间页面; TAB: 组件tab页面 选择了组件数据
             * 此时需要用地址栏中的grandParentId 作为cardId,进行筛选。
             */
            condition = {cardId: this.filter.grandParentId};
        }
        else if (crumbLevel === 2) {
            /**
             * 面包屑:卡片; TAB: 中间tab页面 选择了组件数据
             * 此时需要用地址栏中的parentId 作为cardId,进行筛选。
             */
            condition = {cardId: this.filter.parentId};
        }

        let data = await this.frameRequester(this.prepareQuery(condition));
        this.selectDatasource = data.result;
    }

    async setComponentSummaryHeader() {
        this.materialType = '组件';
        this.crumbData.push({text: '组件数据报告', to: '/stats/material/base/component/list', type: 'link'});

        // 中间页有二级associate，可能会从card/frame跳转过来。二次跳转过来的参数通过parent记录
        let {parentId: associatedId, parentType: associatedType} = this.filter;
        // component 列表查询不需要参数aderId，所以查询时去除下。
        let data = await this.componentRequester(this.prepareQuery({associatedId, associatedType, aderId: null}));

        // 处理下数据，后台返回的列表为instanceId, 与卡片，中间页统一下.
        data.result.forEach(component => {
            component.id = component.instanceId;
        });
        this.selectDatasource = data.result;
    }

    prepareQuery(associatedQuery = {}) {
        // 清洗数据, 除去非查询条件
        let params = omit(this.filter, [
            'associatedType', 'associatedId',
            'parentId', 'parentType',
            'grandParentType', 'grandParentId'
        ]);

        // 组件，中间页有二级associate筛选
        return purify(assign(params, associatedQuery));
    }

    prepareRedirectUrl() {
        return uri.withQuery(
            this.getRoutePath(),
            omit(purify(this.filter), ['pageNo', 'pageSize', 'order', 'orderBy'])
        );
    }
}
